import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Toaster } from "sonner";

import AppRouter from "@/router/AppRouter";
import { queryClient } from "@/services/query-client";
// Stamps the theme on <html> for every route, shell or not.
import "@/store/theme";

/**
 * Keeps the toaster above the dialogs.
 *
 * Every overlay in the app is a native `<dialog>` opened with `showModal()`,
 * which puts it in the browser's **top layer** — painted above everything in
 * the normal document, whatever its z-index. The toaster lives in the normal
 * document, so a message raised while a dialog was open was drawn behind it
 * and never seen. That is most of the messages worth reading: a save refused,
 * a name already taken, a row that would not delete.
 *
 * The top layer is a stack and the last element promoted is on top, so the
 * toaster gets a host of its own that is promoted again every time a dialog
 * opens — after it, and therefore over it. The host is watched rather than the
 * toaster itself because sonner renders nothing at all until the first toast.
 */
function useToastsAboveDialogs(host: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const element = host.current;
    if (!element || !("showPopover" in element)) return;
    const lift = () => {
      try {
        if (element.matches(":popover-open")) element.hidePopover();
        element.showPopover();
      } catch {
        // A browser with no top layer paints it the old way, which is fine.
      }
    };
    lift();
    // `open` is the only attribute that matters: a dialog gaining it has just
    // jumped the queue.
    const watch = new MutationObserver(lift);
    watch.observe(document.body, { subtree: true, attributeFilter: ["open"] });
    return () => watch.disconnect();
  }, [host]);
}

/**
 * Providers + router. `isBrowserRouter` is the shell contract: when the host
 * micro-frontend shell already provides a Router, it passes the flag and
 * AppRouter mounts a fragment so `useLocation` resolves against the host's.
 */
const App = ({ isBrowserRouter }: { isBrowserRouter?: boolean | undefined }) => {
  const host = useRef<HTMLDivElement>(null);
  useToastsAboveDialogs(host);

  return (
    <QueryClientProvider client={queryClient}>
      <AppRouter isBrowserRouter={isBrowserRouter} />
      {/* Top right, under the header: a receipt nobody reads is not a receipt.
          The bottom corner sits below the fold on a long screen and behind the
          theme and language controls on a short one, so it was being missed. */}
      {/* The host draws nothing itself — the toaster inside is `position:
          fixed` — so the UA popover styles are reset away to nothing. */}
      <div
        ref={host}
        popover="manual"
        className="fixed inset-auto m-0 size-0 overflow-visible border-0 bg-transparent p-0"
      >
        <Toaster
          position="top-right"
          gap={10}
          offset={{ top: "76px", right: "20px" }}
          mobileOffset={{ top: "12px", left: "12px", right: "12px" }}
          toastOptions={{
            classNames: {
              toast:
                "rounded-card border border-line bg-surface-raised text-fg text-callout shadow-[var(--shadow-float)] gap-2.5",
              title: "font-medium",
              description: "text-fg-muted text-subhead",
              success: "[&_[data-icon]]:text-success",
              error: "[&_[data-icon]]:text-danger",
              closeButton: "border-line bg-surface-raised text-fg-muted",
            },
          }}
        />
      </div>
    </QueryClientProvider>
  );
};

export default App;
